/**
 * Tabla única de tokens de color del sistema de diseño CLAC.
 *
 * Cada entrada define el valor en modo claro y en modo oscuro. Los valores no se
 * consumen directamente desde los componentes: `uiGlobal.ts` expone cada token como
 * una variable CSS (`var(--clac-…)`) y este archivo es el que define qué valor toma
 * esa variable en cada modo.
 *
 * Consecuencia práctica: cambiar de claro a oscuro no vuelve a renderizar nada, solo
 * cambia el atributo `data-clac-mode` del elemento raíz. Y para ajustar un color en
 * cualquiera de los dos modos solo se toca este archivo.
 *
 * Regla: ningún componente debe llevar un color literal. Si falta un token, se agrega
 * aquí primero y luego se expone en `uiGlobal.ts`.
 */

type TokenPair = readonly [light: string, dark: string];

export const clacColorTokens = {
  // ---- Neutros base -------------------------------------------------------
  // Blanco y negro puros. Se mantienen fijos en los dos modos porque se usan como
  // contraste sobre superficies de color (texto sobre botón primario, por ejemplo).
  'color-white': ['#FFFFFF', '#FFFFFF'],
  'color-black': ['#0F1114', '#0F1114'],

  // ---- Rampa neutra -------------------------------------------------------
  // En claro va de fondos suaves a texto oscuro. En oscuro se invierte el rol:
  // los índices bajos son superficies elevadas y los altos, texto.
  'gray-50': ['#F4F5F7', '#1F2329'],
  'gray-100': ['#E8EAED', '#262B32'],
  'gray-200': ['#C8CDD4', '#333941'],
  'gray-300': ['#A0ABB5', '#454D57'],
  'gray-400': ['#7A8694', '#8B96A3'],
  'gray-600': ['#424B56', '#B6BFC9'],
  'gray-950': ['#0F1114', '#F4F5F7'],

  // ---- Rampa azul (acento) ------------------------------------------------
  'blue-50': ['#E8F2FB', '#132A42'],
  'blue-100': ['#B8D7F2', '#1B3B5C'],
  'blue-200': ['#7BB4E5', '#2F5F8E'],
  'blue-400': ['#2E88D0', '#4A97DE'],
  'blue-600': ['#1E70B9', '#4A97DE'],
  'blue-800': ['#155090', '#9CCBF0'],
  'blue-950': ['#0C3260', '#C7E1F7'],

  // ---- Rampa verde (marca CLAC) -------------------------------------------
  // El verde institucional #BDCD12 no cambia: es identidad de marca. Lo que cambia
  // son los tonos que lo acompañan como fondo o como texto.
  'green-50': ['#F5F9D9', '#22270C'],
  'green-100': ['#E8F0A0', '#333A0F'],
  'green-200': ['#D6E45E', '#4E5714'],
  'green-400': ['#BDCD12', '#BDCD12'],
  'green-600': ['#96A30D', '#A8B811'],
  'green-800': ['#737828', '#C6D53F'],
  'green-950': ['#4A4C10', '#E8F0A0'],

  // ---- Rampa roja (error) -------------------------------------------------
  'red-50': ['#FDF0EB', '#2D1A1A'],
  'red-100': ['#F0B99A', '#5A2020'],
  'red-200': ['#F0B99A', '#7F1D1D'],
  'red-400': ['#D4542A', '#EF6B6B'],
  'red-600': ['#B83232', '#F58A8A'],
  'red-800': ['#8B1A1A', '#FBB4B4'],

  // ---- Rampa ámbar (advertencia) ------------------------------------------
  'amber-50': ['#FDF6E3', '#2A2109'],
  'amber-100': ['#F5D18A', '#4A3A0E'],
  'amber-200': ['#F5D18A', '#5E4A12'],
  'amber-400': ['#C8891A', '#E0A83A'],
  'amber-700': ['#8B5E0A', '#EFC469'],

  // ---- Rampa morada -------------------------------------------------------
  'purple-50': ['#F3EEFB', '#211636'],
  'purple-200': ['#C4A8ED', '#4A2E7A'],
  'purple-700': ['#5B2D9E', '#B79AE8'],
  'purple-900': ['#3A1A6E', '#D4C2F2'],

  // ---- Superficies --------------------------------------------------------
  'bg-page': ['#F4F5F7', '#0F1114'],
  'bg-surface': ['#FFFFFF', '#191C21'],
  'bg-scrim': ['rgba(15,17,20,0.35)', 'rgba(0,0,0,0.62)'],

  // ---- Texto --------------------------------------------------------------
  'text-primary': ['#0F1114', '#F4F5F7'],
  'text-secondary': ['#424B56', '#B6BFC9'],
  'text-muted': ['#7A8694', '#8B96A3'],

  // ---- Bordes -------------------------------------------------------------
  'border-subtle': ['#E8EAED', '#262B32'],
  'border-default': ['#C8CDD4', '#333941'],
  'border-emphasis': ['#7A8694', '#5A646F'],
  'border-focus': ['#1E70B9', '#4A97DE'],
  'border-light': ['#A0ABB5', '#454D57'],

  // ---- Acento (azul) ------------------------------------------------------
  'accent-main': ['#1E70B9', '#4A97DE'],
  'accent-hover': ['#155090', '#7BB4E5'],
  'accent-bg': ['#E8F2FB', '#132A42'],
  'accent-bg-hover': ['#DDEBFA', '#18334F'],
  'accent-border': ['#7BB4E5', '#2F5F8E'],
  'accent-text': ['#155090', '#A8CFF0'],
  'accent-focus-shadow': ['rgba(30,112,185,0.14)', 'rgba(74,151,222,0.28)'],

  // ---- Marca (verde) ------------------------------------------------------
  'brand-bg': ['#F5F9D9', '#22270C'],
  'brand-border': ['#D6E45E', '#4E5714'],
  'brand-dark': ['#737828', '#C6D53F'],
  'brand-primary': ['#96A30D', '#BDCD12'],
  'brand-mark-bg': ['#737828', '#96A30D'],

  // ---- Estados ------------------------------------------------------------
  'danger-bg': ['#FDF0EB', '#2D1A1A'],
  'danger-border': ['#F0B99A', '#7F1D1D'],
  'danger-text': ['#8B1A1A', '#F58A8A'],
  'danger-icon': ['#B83232', '#EF6B6B'],
  'danger-focus-shadow': ['rgba(184,50,50,0.14)', 'rgba(239,107,107,0.28)'],

  'warning-bg': ['#FDF6E3', '#2A2109'],
  'warning-border': ['#F5D18A', '#5E4A12'],
  'warning-text': ['#8B5E0A', '#EFC469'],

  'success-bg': ['#F5F9D9', '#1D2209'],
  'success-border': ['#D6E45E', '#4C5616'],
  'success-text': ['#737828', '#C2D34E'],

  'info-bg': ['#E8F2FB', '#132A42'],
  'info-text': ['#155090', '#A8CFF0'],

  // ---- Rampa neutra semántica ---------------------------------------------
  'n-50': ['#F4F5F7', '#1F2329'],
  'n-100': ['#E8EAED', '#262B32'],
  'n-200': ['#C8CDD4', '#333941'],
  'n-300': ['#A0ABB5', '#454D57'],
  'n-400': ['#7A8694', '#8B96A3'],
  'n-600': ['#424B56', '#B6BFC9'],
  'n-800': ['#1E2229', '#DCE1E6'],
  'n-950': ['#0F1114', '#F4F5F7'],

  // ---- Sombras ------------------------------------------------------------
  // En oscuro la sombra no se ve por luminosidad, así que se sube la opacidad del negro.
  'shadow-card': ['0 1px 3px rgba(15,17,20,.07), 0 1px 2px rgba(15,17,20,.04)', '0 1px 3px rgba(0,0,0,.5), 0 1px 2px rgba(0,0,0,.4)'],
  'shadow-raised': ['0 4px 16px rgba(15,17,20,.09)', '0 4px 16px rgba(0,0,0,.55)'],
  'shadow-modal': ['0 8px 32px rgba(15,17,20,.12)', '0 8px 32px rgba(0,0,0,.62)']
} as const satisfies Record<string, TokenPair>;

export type ClacColorToken = keyof typeof clacColorTokens;
export type ClacThemeMode = 'light' | 'dark';

/** Referencia CSS al token. Es lo que consumen los componentes vía `uiGlobal`. */
export function token(name: ClacColorToken): string {
  return `var(--clac-${name})`;
}

/**
 * Valor literal del token. Solo para renderizadores que no entienden variables CSS:
 * `@react-pdf/renderer` y cualquier librería que dibuje en canvas. Un PDF siempre se
 * imprime sobre papel blanco, así que el modo por defecto es el claro.
 */
export function rawToken(name: ClacColorToken, mode: ClacThemeMode = 'light'): string {
  return clacColorTokens[name][mode === 'dark' ? 1 : 0];
}

const CSS_VAR_PATTERN = /^var\(--clac-([a-z0-9-]+)\)$/;

/**
 * Convierte referencias `var(--clac-…)` a su valor literal de modo claro.
 *
 * Sirve de red de seguridad en la frontera con los reportes PDF: una pantalla puede
 * calcular colores con tokens y pasárselos al reporte, y `@react-pdf/renderer` no
 * resuelve variables CSS. Recorre el objeto completo, así que también cubre campos
 * que se agreguen después.
 */
export function toPrintableColors<T>(value: T): T {
  if (typeof value === 'string') {
    const match = CSS_VAR_PATTERN.exec(value);
    if (!match) return value;
    const name = match[1] as ClacColorToken;
    return (clacColorTokens[name] ? rawToken(name, 'light') : value) as T;
  }
  if (Array.isArray(value)) return value.map(toPrintableColors) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, toPrintableColors(v)])) as T;
  }
  return value;
}

/** Declaraciones `--clac-*` para el modo indicado, listas para un bloque CSS. */
export function cssVariablesFor(mode: ClacThemeMode): Record<string, string> {
  const index = mode === 'dark' ? 1 : 0;
  return Object.fromEntries(Object.entries(clacColorTokens).map(([name, pair]) => [`--clac-${name}`, pair[index]]));
}

/**
 * Rampas de 10 pasos que consume la paleta de Material UI (`themes/theme/clac.ts`).
 *
 * Viven aquí para que este archivo sea el único lugar con valores de color del sistema.
 * A diferencia de los tokens de arriba, estas rampas son hexadecimales reales y no
 * variables CSS: MUI necesita poder calcular sobre ellas (`alpha()`, contraste), y una
 * variable CSS no se puede operar en JavaScript.
 */
export const clacPaletteRamps = {
  light: {
    primary: ['#F5F9D9', '#E8F0A0', '#D6E45E', '#BDCD12', '#BDCD12', '#BDCD12', '#96A30D', '#737828', '#737828', '#4A4C10'],
    secondary: ['#F4F5F7', '#E8EAED', '#C8CDD4', '#7A8694', '#7A8694', '#7A8694', '#424B56', '#1E2229', '#1E2229', '#0F1114'],
    error: ['#FDF0EB', '#F0B99A', '#D4542A', '#D4542A', '#B83232', '#B83232', '#B83232', '#8B1A1A', '#8B1A1A', '#8B1A1A'],
    warning: ['#FDF6E3', '#F5D18A', '#C8891A', '#C8891A', '#C8891A', '#8B5E0A', '#8B5E0A', '#8B5E0A', '#8B5E0A', '#8B5E0A'],
    info: ['#E8F2FB', '#B8D7F2', '#7BB4E5', '#2E88D0', '#1E70B9', '#1E70B9', '#155090', '#0C3260', '#0C3260', '#0C3260'],
    success: ['#F5F9D9', '#E8F0A0', '#96A30D', '#96A30D', '#96A30D', '#737828', '#737828', '#4A4C10', '#4A4C10', '#4A4C10']
  },
  dark: {
    primary: ['#4A4C10', '#737828', '#737828', '#96A30D', '#96A30D', '#BDCD12', '#D6E45E', '#E8F0A0', '#F5F9D9', '#F5F9D9'],
    secondary: ['#0F1114', '#191C21', '#262B32', '#454D57', '#5A646F', '#8B96A3', '#B6BFC9', '#DCE1E6', '#F4F5F7', '#F4F5F7'],
    error: ['#8B1A1A', '#8B1A1A', '#B83232', '#B83232', '#D4542A', '#F0B99A', '#FDF0EB', '#FDF0EB', '#FDF0EB', '#FDF0EB'],
    warning: ['#8B5E0A', '#8B5E0A', '#8B5E0A', '#C8891A', '#C8891A', '#F5D18A', '#FDF6E3', '#FDF6E3', '#FDF6E3', '#FDF6E3'],
    info: ['#0C3260', '#0C3260', '#155090', '#1E70B9', '#1E70B9', '#2E88D0', '#7BB4E5', '#B8D7F2', '#E8F2FB', '#E8F2FB'],
    success: ['#4A4C10', '#4A4C10', '#737828', '#737828', '#96A30D', '#D6E45E', '#E8F0A0', '#F5F9D9', '#F5F9D9', '#F5F9D9']
  }
} as const satisfies Record<ClacThemeMode, Record<string, readonly string[]>>;
